import { faFilePen, faPerson } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Card, CardActions, CardContent, Grid, Stack, Typography, useTheme } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcClickable, MprcIconButtonMoreOptionsDropdown, MprcLink, MprcText, MprcTypography } from 'components';
import { AutoCompleteDataModel } from 'components/MprcAutoComplete';
import { MprcDropdownItems } from 'components/MprcIconButtonDropdown';
import { DevPlanUserDto } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { DashboardPaths } from 'routes/DashboardRouter';
import { UserPanelItem } from 'views/dashboard/UserPanel';
import { NewDirectMessageDialog } from 'views/foundation/directMessage/NewDirectMessageDialog';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    user: DevPlanUserDto;
    onCardClick: () => void;
}
type MenuOptions = "profile" | "analysis" | "directMessage";

////////////////////////////////////////////////////////////////////////////

export const DevPlanUserCard: React.FC<IProps> = ({ user, onCardClick }) => {

    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    const [dialog, setDialog] = useState<any>();

    const menuItems = useMemo(() => {
        const group1 = [] as MprcDropdownItems<MenuOptions>;
        group1.push({ text: t("DevPlan.List.Users.ProfileAction"), value: "profile" });

        if (user.canViewDashboard)
            group1.push({ text: t("DevPlan.List.Users.AnalysisAction"), value: "analysis" });

        group1.push({ text: t("DevPlan.List.Users.DirectMessageAction"), value: "directMessage" }
        );
        return group1;
    }, [user]);

    ///
    const handleSelectOption = (option: MenuOptions) => {
        switch (option) {
            case "profile":
                showUser(user.userId);
                break;
            case "analysis":
                navigate(DashboardPaths.UserPanel, { state: { user: { userId: user.userId, name: user.name, thumbnailUrl: user.thumbnailUrl } as UserPanelItem } });
                break;
            case "directMessage":
                setDialog(
                    <NewDirectMessageDialog
                        recipients={[{ key: user.userId, label: user.name, thumbnailUrl: user.thumbnailUrl } as AutoCompleteDataModel]}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
        }
    };

    var color =
        user.totalItems == 0
            ? theme.palette.muted.light
            : user.completePercent == 100
                ? theme.palette.success.main
                : user.totalDelayedItems > 0
                    ? theme.palette.error.main
                    : theme.palette.primary.main;

    return (
        <>
            {dialog}
            <Card
                key={`${user.userId}`}
                sx={{
                    minWidth: AppImageDimension.ThumbnailWide.width,
                    maxWidth: AppImageDimension.ThumbnailWide.width,
                    boxShadow: "rgba(0,0,0,0.1) 2px 2px 8px 2px",
                    mt: "6px",
                    mr: "8px",
                    borderTop: `9px solid ${color}`
                }}
                elevation={0}
            >
                <CardContent sx={{ p: 1 }}>
                    <MprcClickable onClick={onCardClick}>
                        <Box position="relative">
                            <Stack direction="row" justifyContent="space-between" position="absolute" width="100%">
                                {user.hasDraft ?
                                    <MprcText color="warning">
                                        <FontAwesomeIcon icon={faFilePen} fontSize={16} title={t("DevPlan.List.Users.HasDraftTooltip")} />
                                    </MprcText>
                                    : <>&nbsp;</>
                                }
                                {user.hasPendingApproval &&
                                    <MprcText color="primary">
                                        <FontAwesomeIcon icon={faPerson} fontSize={16} title={t("DevPlan.List.Users.HasPendingApprovalTooltip")} />
                                    </MprcText>
                                }
                            </Stack>
                        </Box>
                        <Stack alignItems="center" spacing={2}>
                            <Avatar variant="circular" src={user.thumbnailUrl} sx={{ width: 110, height: 110 }} />
                            <Stack alignItems="center" spacing={0.5}>
                                <MprcTypography noWrapRows={1} variant="h6" color="black" fontWeight="bold">{user.name}</MprcTypography>
                                <Typography variant="caption" color={user.completePercent == 100 ? "success" : "muted"}>
                                    {user.planAvailableOn != null
                                        ? t("DevPlan.List.Users.PlanAvailableOnText", { date: Formaters.dayMonth(user.planAvailableOn) })
                                        : user.totalPlans == 0
                                            ? t("DevPlan.List.Users.NoActivePlansText")
                                            : user.totalItems == 0
                                                ? t("DevPlan.List.Users.NoActivitiesText")
                                                : t("DevPlan.List.Users.CompleteItemsText", { count: user.completePercent, x: user.totalCompletedItems, y: user.totalItems })}
                                </Typography>
                                <Typography variant="caption" color="error">
                                    {user.totalDelayedItems > 0 ? <Trans t={t} i18nKey={"DevPlan.List.Users.DelayedItemsText"} values={{ count: user.totalDelayedItems }} /> : <>&nbsp;</>}
                                </Typography>
                            </Stack>
                        </Stack>
                    </MprcClickable>
                </CardContent>
                <CardActions disableSpacing sx={{ p: 0, pl: 1, pr: 1, boxSizing: "border-box" }}>
                    <Grid container alignItems="center" justifyContent="space-between" width="100%">
                        <Grid size="grow">
                            <MprcLink variant="overline" onClick={onCardClick}>
                                {t("DevPlan.List.Users.ManagePlansButtonText")}
                            </MprcLink>
                        </Grid>
                        <Grid>
                            <MprcIconButtonMoreOptionsDropdown
                                color="primary"
                                onChange={(selection) => handleSelectOption(selection)}
                                items={menuItems}
                            >
                            </MprcIconButtonMoreOptionsDropdown>
                        </Grid>
                    </Grid>
                </CardActions>
            </Card>
        </>
    );
};
